import { Injectable, inject } from '@angular/core';
import { Router } from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { forkJoin, from, Observable, of } from 'rxjs';
import { concatMap, map, mergeMap, take, tap } from 'rxjs/operators';

import { AuthResource } from './resources/auth.resource';
import { SecurityStorage } from './storages/security.storage';
import { AuthRequestModel } from '../models/auth-request.model';
import { SyncStorage } from './storages/sync.storage';
import { ThumediDatabaseHelper } from '../core/abstractions/thumedi-database.helper';
import { ManagingDataStorage } from './storages/managing-data.storage';

@Injectable({
    providedIn: 'root'
})
export class AuthService {
    private router = inject(Router);
    private securityStorage = inject(SecurityStorage);
    private syncStorage = inject(SyncStorage);
    private authResource = inject(AuthResource);
    private databaseHelper = inject(ThumediDatabaseHelper);
    private ManagingDataStorage = inject(ManagingDataStorage);

    redirectUrl?: string;

    login(data: AuthRequestModel): Observable<void> {
        return this.authResource.login(data).pipe(
            mergeMap(response => {
                const currentUser = this.securityStorage.getUserInfo();

                if (!!currentUser && currentUser.id != response.id) {
                    return from(this.clearAllUserRelatedData()).pipe(
                        map(() => response)
                    );
                } else {
                    return of(response);
                }
            }),
            map(response => this.securityStorage.saveUserInfo(response))
        );
    }

    logout(): void {
        this.clearAllUserRelatedData()
            .pipe(tap(() => this.router.navigate(['login'])))
            .subscribe();
    }

    isLoggedIn(): boolean {
        const userInfo = this.securityStorage.getUserInfo();

        if (userInfo) {
            return !new JwtHelperService().isTokenExpired(userInfo.token);
        }

        return false;
    }

    private clearAllUserRelatedData() {
        return forkJoin([
            from(this.databaseHelper.getTable('bookings').clear()),
            from(this.databaseHelper.getTable('customers').clear()),
            from(this.databaseHelper.getTable('allocations').clear()),
            from(this.databaseHelper.getTable('providers').clear()),
            from(this.databaseHelper.getTable('todos').clear()),
            from(this.databaseHelper.getTable('unsyncedChanges').clear()),
            from(this.databaseHelper.getTable('templates').clear()),
            from(this.databaseHelper.getTable('tableStates').clear())
        ]).pipe(
            tap(() => {
                this.securityStorage.eraseUserInfo();
                this.syncStorage.eraseLastFullSync();
                this.syncStorage.eraseLastSync();
                this.ManagingDataStorage.eraseData();
            })
        );
    }
}
